"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Datumsfeld
// ============================================================
// Jedes `<input type="date">` im Agent geht durch dieses Bauteil,
// auch das in `Input` und in `InlineFeld` (`date-input.test.ts`
// haelt das fest).
//
// Warum es das braucht: Safari schreibt in ein LEERES Datumsfeld
// das heutige Datum — nicht als Wert, sondern als Geisterziffern
// in der normalen Schriftfarbe. Am 27.09.2026 sah es im
// Personenblatt so aus, als habe Christian Barske an diesem Tag
// Geburtstag; eingetragen war keiner. Chrome zeigt „tt.mm.jjjj“,
// Firefox „TT.MM.JJJJ“, beides erkennbar leer — nur Safari nicht.
//
// Das Bauteil markiert ein leeres Feld mit `data-empty`. Die Regel
// dazu steht in `styles/globals.css`: solange das Feld leer ist und
// nicht den Fokus hat, sind die Ziffern des Browsers unsichtbar,
// und an ihrer Stelle steht der Platzhalter in der leisen Farbe.
// Im Fokus zeigt der Browser wieder sein Eigenes — dort wird
// getippt. Firefox kennt weder die Ziffern-Pseudoelemente noch ein
// `::before` am Eingabefeld; er behaelt sein „TT.MM.JJJJ“.
//
// Ein halb getipptes Datum gilt als leer: der Browser liefert es
// als leeren Wert, gespeichert wird davon nichts. Das Feld sagt
// nach dem Verlassen also genau das, was auch gespeichert ist.
//
// Wann „leer“ neu bestimmt wird: beim Zeichnen (gefuehrte Felder
// bekommen ihren Wert von aussen, ohne Ereignis), bei jeder Eingabe
// (ungefuehrte Felder zeichnen dabei nicht neu) und nach dem
// Zuruecksetzen des Formulars.
// ============================================================

import { forwardRef, useCallback, useLayoutEffect, useRef } from "react";
import { cn } from "@/lib/utils";

// ── Rechtsbuendige Datumsfelder ─────────────────────────────
// Bis zum 22.10.2026 nur in den Belegfeldern (`components/beleg/
// felder.tsx`); seitdem nimmt sie auch `InlineFeld`, wenn es
// rechtsbuendig steht (`lage="nebeneinander"`) — dort lag das
// Kalenderzeichen sonst ueber dem Platzhalter eines leeren Feldes.

/**
 * Der Kalenderknopf des Browsers, unsichtbar ueber das ganze
 * Feld gelegt: er nimmt der Zeile keine Breite mehr weg, und
 * der Kalender oeffnet sich bei einem Klick irgendwo im Feld.
 * Getippt wird weiterhin ueber die Tastatur.
 */
export const KALENDER_UEBERALL = cn(
  "relative [&::-webkit-calendar-picker-indicator]:absolute",
  "[&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:m-0",
  "[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full",
  "[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0",
);

/**
 * Die rechte Kante gilt auch fuer das Datum.
 *
 * `text-align` endet bei einem Datumsfeld an der Aussenwand des
 * Systemwidgets: Chrome setzt seine Ziffernfelder in einen
 * eigenen Kasten, der die volle Feldbreite nimmt und seinen
 * Inhalt selbst verteilt — mittig. Das Datum stand dadurch als
 * einzige Angabe der Spalte nicht auf der Kante, auf der „30
 * Tage“, der Ansprechpartner und jede Textzeile daneben enden.
 * Also muss der Kasten selbst nach rechts packen.
 *
 * Safari baut es anders (24.09.2026, dort stand das Datum
 * links): das Feld selbst ist ein Flex-Kasten, und der
 * Ziffernkasten darin ist nur so breit wie die Ziffern — er
 * wird ueber `justify-end` am Feld nach rechts geschoben.
 * WebKit ohne Ziffernfelder zeigt den Wert in
 * `::-webkit-date-and-time-value` und gibt ihm rechts 24 px
 * Rand fuer einen Pfeil mit, den es hier nicht gibt; der Rand
 * faellt weg, `ml-auto` schiebt den Wert an die Kante.
 * Nachgestellt in Playwright-WebKit, im Safari am Mac noch
 * nicht gesehen.
 */
export const ZIFFERN_RECHTS = cn(
  "[&::-webkit-datetime-edit]:flex [&::-webkit-datetime-edit]:justify-end",
  "justify-end",
  "[&::-webkit-date-and-time-value]:mr-0 [&::-webkit-date-and-time-value]:ml-auto",
);

/** Was ein leeres Datumsfeld sagt, wenn der Aufrufer nichts anderes will. */
export const DATE_PLACEHOLDER = "TT.MM.JJJJ";

/**
 * Alles, was ein Eingabefeld nimmt. `type` darf mitkommen, damit das
 * Bauteil gegen ein `<input>` austauschbar ist, und wird uebergangen:
 * es ist immer ein Datum.
 */
export type DateInputProps = React.InputHTMLAttributes<HTMLInputElement>;

function markEmpty(el: HTMLInputElement) {
  el.toggleAttribute("data-empty", el.value === "");
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  // `type` wird herausgenommen und verworfen: es ist immer ein Datum.
  { placeholder = DATE_PLACEHOLDER, type: _type, ...props },
  ref,
) {
  const eigenerRef = useRef<HTMLInputElement | null>(null);

  // Nach jedem Zeichnen: ein gefuehrtes Feld (`value`) und ein
  // ungefuehrtes, dessen `defaultValue` sich geaendert hat, tragen
  // jetzt womoeglich einen anderen Wert — ohne dass ein Ereignis kam.
  useLayoutEffect(() => {
    if (eigenerRef.current) markEmpty(eigenerRef.current);
  });

  const setRef = useCallback(
    (el: HTMLInputElement | null) => {
      eigenerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
      if (!el) return;

      markEmpty(el);
      const pruefen = () => markEmpty(el);
      // `reset` kommt, BEVOR das Formular zuruecksetzt.
      const nachReset = () => window.setTimeout(pruefen, 0);
      const form = el.form;
      el.addEventListener("input", pruefen);
      el.addEventListener("change", pruefen);
      form?.addEventListener("reset", nachReset);
      return () => {
        el.removeEventListener("input", pruefen);
        el.removeEventListener("change", pruefen);
        form?.removeEventListener("reset", nachReset);
        if (typeof ref === "function") ref(null);
        else if (ref) ref.current = null;
      };
    },
    [ref],
  );

  // Schon im ersten HTML richtig, damit Safari vor dem Hydrieren
  // keine Geisterziffern zeigt.
  const anfangsLeer = !(props.value ?? props.defaultValue);

  return (
    <input
      {...props}
      ref={setRef}
      type="date"
      // Der Browser selbst zeigt `placeholder` an einem Datumsfeld
      // nicht; die Regel in globals.css liest ihn aus.
      placeholder={placeholder}
      data-empty={anfangsLeer ? "" : undefined}
    />
  );
});

export { DateInput };
